import React, { useEffect, useState, useRef } from "react";
import { useAppDispatch as useDispatch } from "src/core/redux/hooks";
import { setPageTitle, removeInfoMessage } from "src/core/redux";
import { SentinelBasePage } from "src/web/pages/sentinel-base";
import {
    Alert,
    Box,
    Button,
    Form,
    FormField,
    Input,
    SpaceBetween,
    StatusIndicator,
} from "@cloudscape-design/components";
import { submitPackageDryRunUpgrades, getPackageDryRunUpgradesResult } from "src/core/sentinel/actions";
import { SentinelController } from "src/core/controller";
import { DependencyUpgradeRecommendation, DryRunResult } from "src/core/sentinel/types";
import { DependencyTable } from "src/web/components/DependencyTable";
import { DependencyTableRow } from "src/web/components/DependencyTable/helpers";
import { useSearchParams } from "react-router-dom";

interface DryRunPageProps {
  sentinelController: SentinelController;
}
const toRiskStatus = (upgradeRecommendation: string): string => {
    if (upgradeRecommendation.includes("HIGH_RISK")) return "HIGH_RISK";
    if (upgradeRecommendation.includes("LOW_RISK")) return "LOW_RISK";
    if (upgradeRecommendation.includes("UPGRADE_AVAILABLE")) {
        return "UPGRADE_AVAILABLE";
    }
    return upgradeRecommendation;
};

const toDependencyTableRow = (
    recommendation: DependencyUpgradeRecommendation,
): DependencyTableRow => ({
    dependencyName: recommendation.dependencyName,
    version: recommendation.currentVersion,
    vendorGuidanceStatus: recommendation.currentVersionStatus,
    recommendedVersion: recommendation.upgradableVersion,
    riskStatus: toRiskStatus(recommendation.upgradeRecommendation),
});

export const DryRunPage: React.FC<DryRunPageProps> = ({ sentinelController }) => {
    const dispatch = useDispatch();
    const [searchParams, setSearchParams] = useSearchParams();
    const [packageName, setPackageName] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [polling, setPolling] = useState(false);
    const [dryRunResult, setDryRunResult] = useState<DryRunResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const pollingRef = useRef<NodeJS.Timeout | null>(null);
    const busy = submitting || polling;

    useEffect(() => {
        dispatch(setPageTitle("Dry Run Mode"));
        dispatch(removeInfoMessage({ id: "message_1" }));
        return () => {
            if (pollingRef.current) clearInterval(pollingRef.current);
        };
    }, []);

    useEffect(() => {
        const urlRequestId = searchParams.get("requestId");
        if (urlRequestId) {
            setPolling(true);
            pollForResult(urlRequestId);
        }
    }, []);

    const pollForResult = (requestId: string) => {
        pollingRef.current = setInterval(async () => {
            try {
                const response: any = await dispatch(
                    getPackageDryRunUpgradesResult({ sentinelController, requestId })
                ).unwrap();
                const result = response?.payload as DryRunResult;
                if (result && result.status !== "PROCESSING") {
                    if (pollingRef.current) clearInterval(pollingRef.current);
                    setDryRunResult(result);
                    setPolling(false);
                    if (result.status === "FAILED") {
                        setError(result.errorMessage || "Dry run analysis failed");
                    }
                }
            } catch (e) {
                if (pollingRef.current) clearInterval(pollingRef.current);
                setError(`Failed to poll dry run result for requestId: ${requestId}`);
                setPolling(false);
            }
        }, 5000);
    };

    const handleSubmit = async () => {
        if (!packageName.trim()) return;
        setError(null);
        setDryRunResult(null);
        setSubmitting(true);
        setPolling(false);
        try {
            const response: any = await dispatch(
                submitPackageDryRunUpgrades({ sentinelController, packageName: packageName.trim() })
            ).unwrap();
            setSubmitting(false);
            const result = response?.payload as DryRunResult;
            if (!result) {
                setError(`No response received for package: ${packageName}`);
                return;
            }
            setDryRunResult(result);
            setSearchParams({ requestId: result.requestId });
            if (result.status === "COMPLETED") {
                // Already done, no polling needed
            } else if (result.status === "PROCESSING") {
                setPolling(true);
                pollForResult(result.requestId);
            } else {
                setError(result.errorMessage || `Dry run failed for package: ${packageName}`);
            }
        } catch (e) {
            setSubmitting(false);
            setError(`Failed to submit dry run for package: ${packageName}`);
        }
    };

    const recommendations = dryRunResult?.dependencyUpgradeRecommendations || [];

    return (
        <SentinelBasePage header="Generate Upgrade Recommendation List">
            <SpaceBetween size="l">
                <Form
                    actions={
                        <SpaceBetween direction="vertical" size="s">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button
                                    variant="primary"
                                    disabled={busy || !packageName.trim()}
                                    loading={submitting}
                                    onClick={() => handleSubmit()}
                                >
                  Submit Dry Run
                                </Button>
                            </SpaceBetween>
                        </SpaceBetween>
                    }
                >
                    <FormField>
                        <div className="formfield-margin">
                            <strong>Code Package Name</strong>
                            <Input
                                type="text"
                                placeholder="Please enter package name"
                                value={packageName}
                                onChange={(event) => setPackageName(event.detail.value)}
                                disabled={busy}
                            />
                        </div>
                    </FormField>
                </Form>

                {error && (
                    <Alert type="error" dismissible onDismiss={() => setError(null)}>
                        {error}
                    </Alert>
                )}

                {polling && (!dryRunResult || dryRunResult?.status === "PROCESSING") && (
                    <SpaceBetween size="s">
                        <SpaceBetween size="xs">
                            <Box><strong>Package Name:</strong> {dryRunResult?.packageName || "..."}</Box>
                            <Box><strong>Request ID:</strong> {dryRunResult?.requestId || "..."}</Box>
                            <Box><strong>Dry Run Status:</strong> <StatusIndicator type="loading">PROCESSING</StatusIndicator></Box>
                        </SpaceBetween>
                        <Box textAlign="center">
                            <StatusIndicator type="loading">
                Analyzing dependencies for {dryRunResult?.packageName || "package"}... This may take a few minutes.
                            </StatusIndicator>
                        </Box>
                    </SpaceBetween>
                )}

                {dryRunResult && dryRunResult.status === "COMPLETED" && (
                    <SpaceBetween size="l">
                        <SpaceBetween size="xs">
                            <Box><strong>Package Name:</strong> {dryRunResult?.packageName || "..."}</Box>
                            <Box><strong>Latest Release:</strong> {dryRunResult.latestRelease}</Box>
                            <Box><strong>Dry Run Status:</strong> <StatusIndicator type="success">{dryRunResult.status}</StatusIndicator></Box>
                            <Box><strong>Request ID:</strong> {dryRunResult?.requestId || "..."}</Box>
                            <Box><strong>Total Recommendations:</strong> {dryRunResult.totalRecommendations}</Box>
                        </SpaceBetween>

                        <DependencyTable
                            key={dryRunResult.requestId}
                            items={recommendations.map(toDependencyTableRow)}
                            headerTitle="Dependency Upgrade Recommendations"
                            loadingText="Loading recommendations"
                            emptyText="No recommendations found"
                            filteringPlaceholder="Filter recommendations"
                        />
                    </SpaceBetween>
                )}
            </SpaceBetween>
        </SentinelBasePage>
    );
};
